// components/skeletons/ProposalsListSkeleton.tsx
// Placeholder for the body of the Proposals (templates) page while it loads:
// the categories sidebar on the left and the templates table on the right.
// The page header stays on screen, so it is not drawn here.

import React from 'react';
import { G, C } from '../GlobalComponents/uiTokens';
import { Bone, SkeletonStyles } from './Bone';

interface ProposalsListSkeletonProps {
  /** placeholder rows in the templates table */
  rows?: number;
  /** placeholder rows in the categories sidebar */
  categories?: number;
}

const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

// template · category · channel · placeholders · files · active · updated · actions
const COLS: { w: string; cell: React.ReactNode }[] = [
  {
    w: '240px',
    cell: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Bone w={12} h={12} r={3} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', flex: 1 }}>
          <Bone w="60%" h={10} />
          <Bone w="80%" h={8} />
        </div>
      </div>
    ),
  },
  { w: '110px', cell: <Bone w={70} h={18} r={20} /> },
  { w: '100px', cell: <Bone w={68} h={12} /> },
  { w: '140px', cell: <Bone w="75%" h={10} /> },
  { w: '60px', cell: <Bone w={26} h={10} /> },
  { w: '70px', cell: <Bone w={32} h={18} r={20} /> },
  { w: '100px', cell: <Bone w="70%" h={10} /> },
  { w: '110px', cell: <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}><Bone w={26} h={26} r={7} /><Bone w={26} h={26} r={7} /><Bone w={26} h={26} r={7} /></div> },
];

const ProposalsListSkeleton: React.FC<ProposalsListSkeletonProps> = ({ rows = 6, categories = 5 }) => (
  <div role="status" aria-busy="true" aria-label="Loading proposal templates" className="psk-grid">
    <SkeletonStyles />
    <style>{`
      .psk-grid { display: grid; grid-template-columns: 250px minmax(0, 1fr); gap: 11px; align-items: start; }
      @media (max-width: 960px) { .psk-grid { grid-template-columns: 1fr; } }
    `}</style>

    {/* categories */}
    <div style={SHELL}>
      <div style={{ ...INNER, padding: '10px' }}>
        <div style={{ padding: '4px 6px 10px' }}>
          <Bone w={70} h={8} />
        </div>
        {Array.from({ length: categories + 1 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 8px', marginTop: i === 0 ? 0 : '2px' }}>
            <Bone w={13} h={13} r={4} />
            <Bone w={`${i === 0 ? 62 : 45 + ((i * 17) % 35)}%`} h={10} />
            <Bone w={16} h={10} style={{ marginLeft: 'auto' }} />
          </div>
        ))}
      </div>
    </div>

    {/* templates */}
    <div style={SHELL}>
      <div style={{ ...INNER, overflow: 'hidden' }}>
        <div style={{ padding: '11px 13px', display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap', borderBottom: `1px solid ${G.line}` }}>
          <Bone w={130} h={14} style={{ marginRight: 'auto' }} />
          <Bone w={180} h={34} r={8} />
          <Bone w={44} h={34} r={8} />
          <Bone w={62} h={34} r={8} />
          <Bone w={84} h={34} r={8} />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: '760px' }}>
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${G.line}` }}>
              {COLS.map((c, i) => (
                <div key={i} style={{ width: c.w, flex: i === 0 ? 1 : undefined, flexShrink: 0, padding: '12px 13px' }}>
                  <Bone w="50%" h={8} style={i === COLS.length - 1 ? { marginLeft: 'auto' } : undefined} />
                </div>
              ))}
            </div>
            {Array.from({ length: rows }).map((_, r) => (
              <div key={r} style={{ display: 'flex', alignItems: 'center', borderBottom: r === rows - 1 ? 'none' : `1px solid ${G.softer}` }}>
                {COLS.map((c, i) => (
                  <div key={i} style={{ width: c.w, flex: i === 0 ? 1 : undefined, flexShrink: 0, padding: '11px 13px' }}>
                    {c.cell}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default ProposalsListSkeleton;